import { Grid } from '@mui/material';
import { type FormikContextType, useFormikContext } from 'formik';
import type { FC } from 'react';
import { useContext } from 'react';

import { getUserFacingCategoryName } from '@core/constants/product';
import { SlabContext } from '@core/context/SlabContext';
import type { ActualsMetrics } from '@core/types/api/ActualsMetrics/ActualsMetrics';
import Enums from '@core/types/api/Enums';
import type { ProjectSummary } from '@core/types/api/ProjectSummary/ProjectSummary';
import type { UserInfo } from '@core/types/api/UserInfo/UserInfo';
import {
  getActualsQuantityShortUnitDisplay,
  getActualsQuantityUnitDisplay,
} from '@core/utils/actualsQuantityUnitDisplay';
import { formatSignedPercent } from '@core/utils/decimals';
import { type ExportSheet, toNumberOrNull } from '@core/utils/tableExport';
import { useSlabQuery } from '@web/hooks/useSlabQuery';
import {
  type BacklogFiltersFormikType,
  type MetricItem,
  MetricType,
  filterMetrics,
} from '@web/utils/backlog';
import { backlogQueryFilters } from '@web/utils/backlogFilters';

import { MetricCard } from './components/MetricCard';

const METRIC_TITLES = {
  quotedVolume: 'Quoted Volume',
  forecast: 'Forecast',
  outstandingBacklog: 'Outstanding Backlog',
  thisMonthActual: 'This Month Actual',
} as const;

// A function, not a constant, since headers need tenant units resolved at click-time.
export const metricsExportRows = (
  data: ActualsMetrics | undefined,
  formikBag: FormikContextType<BacklogFiltersFormikType>,
  userInfo: UserInfo,
): ExportSheet => {
  const buildMetricItems = (
    mix: string | null,
    asphalt: string | null,
    aggregate: string | null,
  ): MetricItem[] => [
    { value: mix, category: Enums.ProductCategory.ReadyMix },
    { value: asphalt, category: Enums.ProductCategory.Asphalt },
    { value: aggregate, category: Enums.ProductCategory.Aggregate },
  ];

  const metricRows: { title: string; items: MetricItem[] }[] = [
    {
      title: METRIC_TITLES.quotedVolume,
      items: buildMetricItems(
        data?.quotedVolumeActiveMix ?? null,
        data?.quotedVolumeActiveAsphalt ?? null,
        data?.quotedVolumeActiveAggregate ?? null,
      ),
    },
    {
      title: METRIC_TITLES.forecast,
      items: buildMetricItems(
        data?.forecastActiveMix ?? null,
        data?.forecastActiveAsphalt ?? null,
        data?.forecastActiveAggregate ?? null,
      ),
    },
    {
      title: METRIC_TITLES.outstandingBacklog,
      items: buildMetricItems(
        data?.outstandingBacklogActiveMix ?? null,
        data?.outstandingBacklogActiveAsphalt ?? null,
        data?.outstandingBacklogActiveAggregate ?? null,
      ),
    },
    {
      title: METRIC_TITLES.thisMonthActual,
      items: buildMetricItems(
        data?.actualCurrentMonthMix ?? null,
        data?.actualCurrentMonthAsphalt ?? null,
        data?.actualCurrentMonthAggregate ?? null,
      ),
    },
  ];

  const filteredRows = metricRows.map((row) => ({
    title: row.title,
    items: filterMetrics(row.items, formikBag, userInfo),
  }));

  const includedCategories = (filteredRows[0]?.items ?? [])
    .map((item) => item.category)
    .filter((category): category is Enums.ProductCategory => category !== undefined);

  const headers = [
    'Metric',
    ...includedCategories.map(
      (category) =>
        `${getUserFacingCategoryName(category)} (${getActualsQuantityShortUnitDisplay(category)})`,
    ),
  ];

  const rows = filteredRows.map((row) => [
    row.title,
    ...includedCategories.map((category) =>
      toNumberOrNull(row.items.find((item) => item.category === category)?.value ?? null),
    ),
  ]);

  return { name: 'Metrics', headers, rows };
};

type MetricCardsProps = {
  projectIDs: string[];
};

export const MetricCards: FC<MetricCardsProps> = ({ projectIDs }) => {
  const formikBag = useFormikContext<BacklogFiltersFormikType>();
  const ctx = useContext(SlabContext);

  const { data, isLoading } = useSlabQuery('GET actuals metrics', {
    queryParams: {
      filterBy: [
        ...(projectIDs.length
          ? [
              {
                operation: Enums.FilterOperation.Equals,
                name: 'id' as const,
                values: projectIDs,
              },
            ]
          : []),
        ...backlogQueryFilters<ProjectSummary>(formikBag.values),
      ],
    },
  });

  const readyMixUnit = getActualsQuantityUnitDisplay(Enums.ProductCategory.ReadyMix);
  const asphaltUnit = getActualsQuantityUnitDisplay(Enums.ProductCategory.Asphalt);
  const aggregatesUnit = getActualsQuantityUnitDisplay(Enums.ProductCategory.Aggregate);

  return (
    <Grid container spacing={3} sx={{ marginTop: 2 }}>
      <Grid item xs={12} sm={6} md={3} key={MetricType.Volume}>
        <MetricCard
          infoTooltip={`Total quantity quoted across all active projects, using quotes exported to dispatch. Unit reflects product type (${readyMixUnit} for ready-mix, ${aggregatesUnit} for aggregates, and ${asphaltUnit} for asphalt). If an estimated quantity override has been entered in the Details section of a project (used when product quantities aren't yet known), that value is used instead of line item quantities.`}
          isLoading={isLoading}
          title={METRIC_TITLES.quotedVolume}
          subTitle='Total Quoted Work sent to Dispatch'
          type={MetricType.Volume}
          metricItems={filterMetrics(
            [
              {
                value: data?.quotedVolumeActiveMix ?? null,
                unit: readyMixUnit,
                status: formatSignedPercent(data?.quotedVolumeChangePercentageMix ?? 0),
                category: Enums.ProductCategory.ReadyMix,
              },
              {
                value: data?.quotedVolumeActiveAsphalt ?? null,
                unit: asphaltUnit,
                status: formatSignedPercent(data?.quotedVolumeChangePercentageAsphalt ?? 0),
                category: Enums.ProductCategory.Asphalt,
              },
              {
                value: data?.quotedVolumeActiveAggregate ?? null,
                unit: aggregatesUnit,
                status: formatSignedPercent(data?.quotedVolumeChangePercentageAggregate ?? 0),
                category: Enums.ProductCategory.Aggregate,
              },
            ],
            formikBag,
            ctx.userInfo,
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3} key={MetricType.Forecast}>
        <MetricCard
          isLoading={isLoading}
          title={METRIC_TITLES.forecast}
          subTitle='Based on project forecasts'
          type={MetricType.Forecast}
          infoTooltip='Projected quantity based on forecasts entered at the project level. Weighted by confidence value where set. If no confidence value is entered, the forecast is used as-is. Unit reflects product type.'
          metricItems={filterMetrics(
            [
              {
                value: data?.forecastActiveMix ?? null,
                unit: readyMixUnit,
                status: formatSignedPercent(data?.forecastChangePercentageMix ?? 0),
                category: Enums.ProductCategory.ReadyMix,
              },
              {
                value: data?.forecastActiveAsphalt ?? null,
                unit: asphaltUnit,
                status: formatSignedPercent(data?.forecastChangePercentageAsphalt ?? 0),
                category: Enums.ProductCategory.Asphalt,
              },
              {
                value: data?.forecastActiveAggregate ?? null,
                unit: aggregatesUnit,
                status: formatSignedPercent(data?.forecastChangePercentageAggregate ?? 0),
                category: Enums.ProductCategory.Aggregate,
              },
            ],
            formikBag,
            ctx.userInfo,
          )}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3} key={MetricType.Backlog}>
        <MetricCard
          isLoading={isLoading}
          title={METRIC_TITLES.outstandingBacklog}
          subTitle='Sold but not yet delivered'
          type={MetricType.Backlog}
          infoTooltip='Quantity sold but not yet delivered. Calculated as accepted quote quantity exported to dispatch, minus all quantity delivered to date. Counts every project with volume still outstanding, whether or not it has started or has dates entered. Unit reflects product type.'
          metricItems={filterMetrics(
            [
              {
                value: data?.outstandingBacklogActiveMix ?? null,
                unit: readyMixUnit,
                status: formatSignedPercent(data?.outstandingBacklogChangePercentageMix ?? 0),
                category: Enums.ProductCategory.ReadyMix,
              },
              {
                value: data?.outstandingBacklogActiveAsphalt ?? null,
                unit: asphaltUnit,
                status: formatSignedPercent(data?.outstandingBacklogChangePercentageAsphalt ?? 0),
                category: Enums.ProductCategory.Asphalt,
              },
              {
                value: data?.outstandingBacklogActiveAggregate ?? null,
                unit: aggregatesUnit,
                status: formatSignedPercent(data?.outstandingBacklogChangePercentageAggregate ?? 0),
                category: Enums.ProductCategory.Aggregate,
              },
            ],
            formikBag,
            ctx.userInfo,
          )}
        />
      </Grid>

      <Grid item xs={12} sm={6} md={3} key={MetricType.Actual}>
        <MetricCard
          isLoading={isLoading}
          title={METRIC_TITLES.thisMonthActual}
          subTitle='Current month delivered'
          type={MetricType.Actual}
          infoTooltip='Total quantity delivered to customers so far this month, pulled from dispatch. Unit reflects product type.'
          metricItems={filterMetrics(
            [
              {
                value: data?.actualCurrentMonthMix ?? null,
                unit: readyMixUnit,
                status: formatSignedPercent(data?.actualChangePercentageMix ?? 0),
                category: Enums.ProductCategory.ReadyMix,
              },
              {
                value: data?.actualCurrentMonthAsphalt ?? null,
                unit: asphaltUnit,
                status: formatSignedPercent(data?.actualChangePercentageAsphalt ?? 0),
                category: Enums.ProductCategory.Asphalt,
              },
              {
                value: data?.actualCurrentMonthAggregate ?? null,
                unit: aggregatesUnit,
                status: formatSignedPercent(data?.actualChangePercentageAggregate ?? 0),
                category: Enums.ProductCategory.Aggregate,
              },
            ],
            formikBag,
            ctx.userInfo,
          )}
        />
      </Grid>
    </Grid>
  );
};
